import get from 'lodash.get';
import React, { createContext, useContext, useMemo } from 'react';
import ChannelContext from '../components/ChannelContext';
import { useShopConfig } from '../hooks/useShopConfig';
import { gtagTracker } from './integrations/gtag';
import { gtmTracker } from './integrations/gtm';
import { relewareTracker } from './integrations/releware';
import { trackerRegistry } from './trackerRegistry';
import Helmet from 'react-helmet-async';
import { usePreconnectLinks } from '../hooks/usePreconnectLinks';
const initialAnalyticsConfig = {
    gtag: { trackingId: null, useArticleNumberAsId: false },
    gtm: { containerId: null },
    releware: {
        enabled: false,
        baseUrl: null,
        uid: null
    }
};
export const AnalyticsContext = createContext({ config: initialAnalyticsConfig, trackerRegistry: null });
export const getGoogleAnalytics = (config, selectedChannel) => {
    // Channel overrides are used in order to support channel specific overrides for certain shop config variables. You can read more here: https://docs.dev.jetshop.se/docs-getting-started-shop-config#other-options
    const channelOverrides = config.channelOverrides
        ? config.channelOverrides[selectedChannel.name]
        : {};
    let { trackingID, tagManagerID, useArticleNumberAsId } = Object.assign(Object.assign({}, config), channelOverrides);
    if (get(selectedChannel, 'settings.tracking.ga.trackingId')) {
        trackingID = selectedChannel.settings.tracking.ga.trackingId;
    }
    if (get(selectedChannel, 'settings.tracking.ga.useArticleNumberAsId')) {
        useArticleNumberAsId =
            selectedChannel.settings.tracking.ga.useArticleNumberAsId;
    }
    if (get(selectedChannel, 'settings.tracking.gtm.containerId')) {
        tagManagerID = selectedChannel.settings.tracking.gtm.containerId;
    }
    return { trackingID, useArticleNumberAsId, tagManagerID };
};
const AnalyticsProvider = function AnalyticsProvider(props) {
    var _a, _b, _c, _d, _e;
    const shopConfig = useShopConfig();
    const { selectedChannel } = useContext(ChannelContext);
    const { tagManagerID, trackingID, useArticleNumberAsId } = getGoogleAnalytics(shopConfig, selectedChannel);
    const analyticsConfig = React.useMemo(() => ({
        gtag: {
            trackingId: trackingID,
            useArticleNumberAsId: useArticleNumberAsId
        },
        gtm: {
            containerId: tagManagerID
        },
        releware: props.releware
    }), [props.releware, tagManagerID, trackingID, useArticleNumberAsId]);
    const { gtm, gtag, releware } = analyticsConfig;
    const registry = useMemo(() => trackerRegistry({
        config: analyticsConfig,
        inServer: __IN_SERVER__
    }), [analyticsConfig]);
    if ((_a = gtag) === null || _a === void 0 ? void 0 : _a.trackingId) {
        registry.add(gtagTracker({
            currency: selectedChannel.currency.format.code,
            trackingId: gtag.trackingId,
            useArticleNumberAsId: gtag.useArticleNumberAsId
        }));
    }
    if ((_b = releware) === null || _b === void 0 ? void 0 : _b.enabled) {
        registry.add(relewareTracker(releware));
    }
    if ((_c = gtm) === null || _c === void 0 ? void 0 : _c.containerId) {
        registry.add(gtmTracker({ containerId: gtm.containerId }));
    }
    if (shopConfig.trackers) {
        shopConfig.trackers.forEach(tracker => {
            registry.add(tracker);
        });
    }
    const value = useMemo(() => {
        return {
            config: analyticsConfig,
            trackerRegistry: registry
        };
    }, [analyticsConfig, registry]);
    const preconnectLinks = usePreconnectLinks({
        ga: !!((_d = gtag) === null || _d === void 0 ? void 0 : _d.trackingId),
        gtm: !!((_e = gtm) === null || _e === void 0 ? void 0 : _e.containerId)
    });
    return (React.createElement(AnalyticsContext.Provider, { value: value },
        React.createElement(Helmet, null, preconnectLinks.map(({ href, crossOrigin }) => (React.createElement("link", Object.assign({ rel: "preconnect", href: href }, (crossOrigin && { crossOrigin }), { key: href }))))),
        props.children));
};
export default AnalyticsProvider;
//# sourceMappingURL=AnalyticsProvider.js.map